/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at <http://mozilla.org/MPL/2.0/>. */

//

import type { Context } from "devtools/client/debugger/src/reducers/pause";
import { copyToClipboard as copyTextToClipboard } from "replay-next/components/sources/utils/clipboard";
import {
  Source,
  sourcesByIdCache,
  streamingSourceContentsCache,
} from "replay-next/src/suspense/SourcesCache";
import type { UIThunkAction } from "ui/actions";

import { closeQuickOpen } from "../reducers/quick-open";
import {
  ActiveSearchType,
  HighlightedRange,
  clearHighlightLineRange,
  highlightLineRange,
  setCursorPosition,
  setPrimaryPaneTab,
  toggleActiveSearch,
} from "../reducers/ui";
import { getActiveSearch, getContext, getQuickOpenEnabled } from "../selectors";
import { selectSource } from "./sources/select";

export {
  closeActiveSearch,
  clearHighlightLineRange,
  closeProjectSearch,
  focusFullTextInput,
  highlightLineRange,
  setCursorPosition,
  setFullTextQuery,
  setPrimaryPaneTab,
  setShownSource,
  setViewport,
  toggleActiveSearch,
  toggleFrameworkGrouping,
} from "../reducers/ui";

export function setActiveSearch(activeSearch: ActiveSearchType): UIThunkAction {
  return (dispatch, getState) => {
    const activeSearchState = getActiveSearch(getState());
    if (activeSearchState === activeSearch) {
      return;
    }

    if (getQuickOpenEnabled(getState())) {
      dispatch(closeQuickOpen());
    }

    dispatch(toggleActiveSearch(activeSearch));
  };
}

// Preserve existing export names
export const updateCursorPosition = setCursorPosition;

export function openSourceLink(sourceId: string, line?: number, column?: number): UIThunkAction {
  return async (dispatch, getState) => {
    const cx = getContext(getState());
    const location = { sourceId, line, column };

    dispatch(showSource(cx, sourceId));
    await dispatch(selectSource(cx, sourceId, location));
  };
}

export function showSource(cx: Context, sourceId: string, openSource = true): UIThunkAction {
  return (dispatch, getState, { replayClient }) => {
    const sourcesById = sourcesByIdCache.getValueIfCached(replayClient);
    const source = sourcesById?.get(sourceId);

    if (!source) {
      return;
    }

    dispatch(setPrimaryPaneTab("sources"));
    // @ts-ignore apparently empty options is legal
    dispatch(selectSource(cx, source.id, {}, openSource));
  };
}

export function flashLineRange(location: HighlightedRange): UIThunkAction {
  return dispatch => {
    dispatch(highlightLineRange(location));
    setTimeout(() => dispatch(clearHighlightLineRange()), 200);
  };
}

export function copyToClipboard(source: Source): UIThunkAction {
  return (dispatch, getState, { replayClient }) => {
    const streaming = streamingSourceContentsCache.stream(replayClient, source.id);
    if (streaming?.complete) {
      copyTextToClipboard(streaming.value!);
    }
  };
}
